import { Activity, Loader2, XOctagon } from 'lucide-react';
import { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@easyflow/ui-master/components/ui/card';
import { Badge } from '@easyflow/ui-master/components/ui/badge';
import { Button } from '@easyflow/ui-master/components/ui/button';
import { Progress } from '@easyflow/ui-master/components/ui/progress';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@easyflow/ui-master/components/ui/table';
import { TerminateBackendDialog } from './TerminateBackendDialog';
import type { ConnectionStats, LongRunningQuery, TerminateBackendRequest } from '@easyflow/shared';

interface Props {
  connections: ConnectionStats;
  longQueries: LongRunningQuery[];
  onTerminated: () => void;
}

const STATE_COLOR: Record<string, string> = {
  active: 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300',
  idle: 'bg-slate-300/40 text-foreground dark:text-muted-foreground/60',
  'idle in transaction': 'bg-amber-500/15 text-amber-700 dark:text-amber-300',
  'idle in transaction (aborted)': 'bg-rose-500/15 text-rose-700 dark:text-rose-300',
  fastpath: 'bg-cyan-500/15 text-cyan-700 dark:text-cyan-300',
  disabled: 'bg-slate-300/40 text-muted-foreground',
};

function durationLabel(seconds: number): string {
  if (seconds < 60) return `${seconds.toFixed(0)}s`;
  if (seconds < 3600) return `${(seconds / 60).toFixed(1)}m`;
  return `${(seconds / 3600).toFixed(1)}h`;
}

export function ConnectionsCard({ connections, longQueries, onTerminated }: Props) {
  const [terminateTarget, setTerminateTarget] = useState<{ pid: number; mode: TerminateBackendRequest['mode'] } | null>(null);
  const pct = connections.max_connections > 0
    ? Math.min(100, Math.round((connections.total / connections.max_connections) * 100))
    : 0;
  const pctCls = pct >= 90 ? 'text-rose-600' : pct >= 75 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Activity className="h-5 w-5 text-cyan-500" />
          Connessioni
          {connections.waiting > 0 && (
            <Badge variant="outline" className="ml-1 bg-amber-500/15 text-amber-700 border-amber-300">
              {connections.waiting} waiting
            </Badge>
          )}
        </CardTitle>
        <CardDescription>
          {connections.total} / {connections.max_connections} backend client (
          <span className={`font-semibold ${pctCls}`}>{pct}%</span>) — {connections.waiting} in wait_event
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <Progress value={pct} className="h-2" />
        </div>

        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Per stato</p>
          <div className="flex flex-wrap gap-2">
            {connections.by_state.length === 0 ? (
              <span className="text-sm text-muted-foreground italic">Nessuna connessione client</span>
            ) : connections.by_state.map((s) => (
              <Badge key={s.state} variant="outline" className={`gap-1 ${STATE_COLOR[s.state] ?? STATE_COLOR.disabled}`}>
                <span className="font-mono text-xs">{s.state}</span>
                <span className="font-semibold">{s.count}</span>
              </Badge>
            ))}
          </div>
        </div>

        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">Top 10 client_addr</p>
          {connections.by_client.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nessun client remoto</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Conn</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {connections.by_client.map((c) => (
                  <TableRow key={c.client_addr}>
                    <TableCell className="font-mono text-xs">{c.client_addr}</TableCell>
                    <TableCell className="text-right font-mono text-xs">{c.count}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>

        <div>
          <p className="text-xs font-semibold uppercase text-muted-foreground mb-2">
            Long-running queries (&gt; 30s) — {longQueries.length}
          </p>
          {longQueries.length === 0 ? (
            <p className="text-sm text-muted-foreground italic">Nessuna query lunga in corso</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>PID</TableHead>
                  <TableHead>App</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead className="text-right">Durata</TableHead>
                  <TableHead>Wait</TableHead>
                  <TableHead>Query</TableHead>
                  <TableHead></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {longQueries.map((q) => (
                  <TableRow key={q.pid}>
                    <TableCell className="font-mono text-xs">{q.pid}</TableCell>
                    <TableCell className="text-xs">{q.application_name ?? '—'}</TableCell>
                    <TableCell className="font-mono text-xs">{q.client_addr ?? '—'}</TableCell>
                    <TableCell className="text-right font-mono text-xs text-amber-600">
                      {durationLabel(q.duration_seconds)}
                    </TableCell>
                    <TableCell className="text-xs text-muted-foreground">
                      {q.wait_event_type ? `${q.wait_event_type}/${q.wait_event ?? ''}` : '—'}
                    </TableCell>
                    <TableCell className="text-xs font-mono max-w-md truncate" title={q.query}>{q.query}</TableCell>
                    <TableCell className="text-right">
                      <Button
                        variant="outline"
                        size="sm"
                        className="h-7 text-xs gap-1"
                        onClick={() => setTerminateTarget({ pid: q.pid, mode: 'cancel' })}
                      >
                        <XOctagon className="h-3 w-3" />
                        Cancel
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </div>
      </CardContent>

      <TerminateBackendDialog
        target={terminateTarget}
        onClose={() => setTerminateTarget(null)}
        onSuccess={() => {
          setTerminateTarget(null);
          onTerminated();
        }}
      />
    </Card>
  );
}

export function ConnectionsCardLoading() {
  return (
    <Card>
      <CardContent className="py-10 flex items-center justify-center">
        <Loader2 className="h-5 w-5 animate-spin text-cyan-500" />
      </CardContent>
    </Card>
  );
}
